import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, storageRef } from "../../Environment/firebase";
import { useWindowDimensions } from "../../Functions/ScreenOperation/useWindowDimension";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  FormGroup,
  Button,
  Container,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { lightBlue, grey } from "@mui/material/colors";
import { CheckCircle, Cancel, MenuBook, ExpandMore, NavigateBefore, NavigateNext, Refresh, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { MstaFontFamily } from "../../Functions/Style/MstaFont";

type QuizItem = {
  id: string;
  type: "single_choice" | "multiple_choice";
  questionText: string;
  options: string[];
  correctAnswers: number[];
};

type CaseData = {
  status: string;
  questionNumber?: number;
  caseInfo: {
    age: number;
    gender: string;
    history: string;
  };
  tags: string[];
  departmentTags: string[];
  imageUrls: string[];
  quizItems: QuizItem[];
  closing: {
    explanation: string;
    referenceUrl: string;
  };
};

export default function MStudyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const [data, setData] = useState<CaseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [answers, setAnswers] = useState<{ [quizId: string]: number[] }>({});
  const [showResults, setShowResults] = useState<{ [quizId: string]: boolean }>({});
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState<{ [key: number]: boolean }>({});
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        const docRef = db.collection("questions").doc(id);
        const docSnap = await docRef.get();

        if (docSnap.exists) {
          const docData = docSnap.data() as CaseData;
          if (docData.imageUrls && docData.imageUrls.length > 0) {
            const resolvedUrls = await Promise.all(
              docData.imageUrls.map(async (urlPath) => {
                if (urlPath.startsWith("http")) return urlPath;
                try {
                  return await storageRef.child(urlPath).getDownloadURL();
                } catch (e) {
                  console.warn("画像URLの取得に失敗しました:", urlPath);
                  return urlPath;
                }
              })
            );
            docData.imageUrls = resolvedUrls;
          }
          setData(docData);
        } else {
          setError("指定された問題が見つかりません。");
        }
      } catch (err) {
        console.error("Error fetching fetching document: ", err);
        setError("データの取得に失敗しました。");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleSingleChoiceChange = (quizId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [quizId]: [parseInt(value, 10)] }));
  };

  const handleMultipleChoiceChange = (quizId: string, index: number, isChecked: boolean) => {
    setAnswers((prev) => {
      const current = prev[quizId] || [];
      if (isChecked) {
        return { ...prev, [quizId]: [...current, index] };
      } else {
        return { ...prev, [quizId]: current.filter((i) => i !== index) };
      }
    });
  };

  const checkAnswer = (quizId: string) => {
    setShowResults((prev) => ({ ...prev, [quizId]: true }));
  };

  const handleReset = () => {
    setResetDialogOpen(true);
  };

  const handleResetConfirm = () => {
    setAnswers({});
    setShowResults({});
    setCurrentQuizIndex(0);
    setResetDialogOpen(false);
    setResultDialogOpen(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="95vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="md" sx={{ mt: 5, minHeight: "95vh" }}>
        <Typography color="error" variant="h5" align="center">{error}</Typography>
      </Container>
    );
  }

  if (data.status !== "published") {
    return (
      <Container maxWidth="md" sx={{ mt: 5, minHeight: "95vh", textAlign: "left" }}>
        <Typography color="error" variant="h5" align="center">この問題は現在非公開（下書き）です。</Typography>
      </Container>
    );
  }

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <>
      {width >= 1300 && (
        <Box
          sx={{
            position: "absolute",
            left: "calc((100vw - 1200px) / 2 - 36px)",
            top: "96px",
          }}
        >
          <IconButton
            sx={{
              color: "#9CA4AD",
              border: "1px solid #9CA4AD",
            }}
            onClick={handleBackClick}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
      )}

    <Container maxWidth="lg" sx={{ mt: 4, mb: 10, minHeight: "95vh", textAlign: "left", fontFamily: MstaFontFamily, position: "relative" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        {width < 1300 && width >= 600 && (
          <Box sx={{ mr: 2 }}>
            <IconButton
              sx={{
                color: "#9CA4AD",
                border: "1px solid #9CA4AD",
              }}
              onClick={handleBackClick}
            >
              <ArrowBackIcon />
            </IconButton>
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }}>
      
      {/* ページタイトル (症例情報) */}
      <Typography
        gutterBottom
        align="left"
        variant="h4"
        fontFamily={MstaFontFamily}
      >
        {data.questionNumber ? `No.${data.questionNumber}` : "問題演習"}
      </Typography>

      <Divider
        sx={{
          borderBottomWidth: 1.5,
          borderColor: "black",
          mt: 1,
          mb: 4,
        }}
      />

      <Grid container spacing={{ xs: 4, md: 6 }}>
        {/* 左カラム：症例情報と画像 */}
        <Grid item xs={12} md={6}>
          <Box sx={{ pr: { md: 2 }, pb: { md: 4 } }}>
            {/* 症例情報 */}
        <Box sx={{ mb: 4, mt: { xs: 2, md: 0 } }}>
          <Grid container spacing={0} direction="column" sx={{ borderRadius: 1, overflow: "hidden" }}>
            <Grid item sx={{ bgcolor: "#ECF5FA", p: 1.5, pb: 0.5 }}>
              <Typography fontFamily={MstaFontFamily} sx={{ lineHeight: 1.8, fontSize: "1.25rem", color: grey[900], fontWeight: "bold" }}>
                {data.caseInfo.age ? (data.caseInfo.age < 10 ? "10歳未満" : data.caseInfo.age >= 100 ? "100歳以上" : `${Math.floor(data.caseInfo.age / 10) * 10}代`) : "年齢不明"}　{data.caseInfo.gender === "male" ? "男性" : data.caseInfo.gender === "female" ? "女性" : "その他"}
              </Typography>
            </Grid>
            <Grid item sx={{ bgcolor: "#ECF5FA", p: 1.5, pt: 0.5 }}>
              <Typography
                paragraph
                textAlign="justify"
                fontFamily={MstaFontFamily}
                style={{ whiteSpace: "pre-line" }}
                sx={{ m: 0, lineHeight: 1.8, fontSize: "1rem", color: grey[900] }}
              >
                {data.caseInfo.history || "記載なし"}
              </Typography>
            </Grid>
          </Grid>
        </Box>

      {/* 画像 */}
      {data.imageUrls.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 1, bgcolor: grey[50] }}>
            <Grid container spacing={3}>
              {data.imageUrls.map((url, idx) => (
                <Grid item xs={12} key={idx}>
                  {!imageLoaded[idx] && (
                    <Skeleton 
                      variant="rectangular" 
                      width="100%" 
                      height={400} 
                      animation="wave" 
                      sx={{ borderRadius: 1, bgcolor: grey[200] }} 
                    />
                  )}
                  <Box 
                    component="img" 
                    src={url} 
                    alt={`画像 ${idx + 1}`} 
                    onLoad={() => setImageLoaded((prev) => ({ ...prev, [idx]: true }))}
                    sx={{ width: "100%", borderRadius: 1, bgcolor: "#fff", display: imageLoaded[idx] ? "block" : "none" }}                    />
                  <Typography variant="subtitle1" align="center" fontFamily={MstaFontFamily} fontWeight="bold" sx={{ mt: 1, color: grey[800] }}>
                    画像{idx + 1}
                  </Typography>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Box>
        )}
      {/* 解説・出典 (左カラムの最後・アコーディオンで初期は閉じている) */}
      {data.closing && (data.closing.explanation || data.closing.referenceUrl) && (
        <Accordion elevation={0} sx={{ mt: 4, mb: 4, bgcolor: grey[50], border: "1px solid", borderColor: grey[300], borderRadius: "4px !important", '&:before': { display: 'none' }}}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, pt: 1, pb: 1}}>
            <Typography variant="h6" fontWeight="bold" fontFamily={MstaFontFamily} sx={{ display: "flex", alignItems: "center", gap: 1, color: lightBlue[800] }}>
              <MenuBook sx={{ color: lightBlue[400] }} /> 解説・出典
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 4, pb: 4, pt: 0 }}>
            {data.closing.explanation && (
              <Typography variant="body1" fontFamily={MstaFontFamily} paragraph sx={{ mt: 1, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
                {data.closing.explanation}
              </Typography>
            )}
            {data.closing.referenceUrl && (
              <Box sx={{ mt: 3 }}>
                <Button 
                  variant="outlined" 
                  size="medium"
                  href={data.closing.referenceUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  sx={{ px: 4, py: 1, fontWeight: "bold", fontSize: "1rem", color: lightBlue[600], borderColor: lightBlue[400], borderWidth: 2, borderRadius: 1, fontFamily: MstaFontFamily, textTransform: "none", '&:hover': { borderColor: lightBlue[600], borderWidth: 2, bgcolor: 'rgba(3, 169, 244, 0.04)' } }}
                >
                  参考URLを開く
                </Button>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}
          </Box>
        </Grid>

        {/* 右カラム：設問 */}
        <Grid item xs={12} md={6}>

      {/* 設問 */}
      {(() => {
        const quiz = data.quizItems[currentQuizIndex];
        const quizIndex = currentQuizIndex;
        const userAnswers = answers[quiz.id] || [];
        const isShowResult = showResults[quiz.id];

        // 判定ロジック：配列同士の比較
        const isCorrect = 
          userAnswers.length === quiz.correctAnswers.length &&
          userAnswers.every((val) => quiz.correctAnswers.includes(val));

        return (
          <Card elevation={1} sx={{ mb: 5, borderRadius: 1, overflow: "hidden" }} key={quiz.id}>
            <Box sx={{ bgcolor: lightBlue[400], color: "#fff", p: 1.5, px: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="subtitle1" fontWeight="bold" fontFamily={MstaFontFamily}>
                問題 {quizIndex + 1}
              </Typography>
              <Typography variant="body2" fontFamily={MstaFontFamily} sx={{ opacity: 0.9 }}>
                全{data.quizItems.length}問
              </Typography>
            </Box>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" fontFamily={MstaFontFamily} sx={{ fontWeight: 500, lineHeight: 1.6, mb: 4, whiteSpace: "pre-wrap" }}>
                {quiz.questionText}
              </Typography>

              <Box sx={{ bgcolor: "#f9f9f9", p: 3, borderRadius: 2 }}>
                {quiz.type === "single_choice" ? (
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ mb: 2, fontWeight: "bold", color: "text.secondary" }}>
                      選択肢を一つ選んでください
                    </FormLabel>
                    <RadioGroup
                      value={userAnswers.length > 0 ? userAnswers[0].toString() : ""}
                      onChange={(e) => handleSingleChoiceChange(quiz.id, e.target.value)}
                    >
                      {quiz.options.map((opt, optIdx) => (
                        <FormControlLabel
                          key={optIdx}
                          value={optIdx.toString()}
                          control={<Radio sx={{ color: lightBlue[400], '&.Mui-checked': { color: lightBlue[400] } }} />}
                          label={<Typography variant="body1" fontFamily={MstaFontFamily}>{opt}</Typography>}
                          disabled={isShowResult}
                          sx={{ mx: 0, mb: 1, '&:hover': { bgcolor: "rgba(0,0,0,0.04)" }, borderRadius: 1, p: 0.5, pr: 2 }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                ) : (
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ mb: 2, fontWeight: "bold", color: "text.secondary", fontFamily: MstaFontFamily }}>
                      該当するものをすべて選んでください
                    </FormLabel>
                    <FormGroup>
                      {quiz.options.map((opt, optIdx) => (
                        <FormControlLabel
                          key={optIdx}
                          control={
                            <Checkbox 
                              sx={{ color: lightBlue[400], '&.Mui-checked': { color: lightBlue[400] } }}
                              checked={userAnswers.includes(optIdx)}
                              onChange={(e) => handleMultipleChoiceChange(quiz.id, optIdx, e.target.checked)}
                              name={opt} 
                            />
                          }
                          label={<Typography variant="body1" fontFamily={MstaFontFamily}>{opt}</Typography>}
                          disabled={isShowResult}
                          sx={{ mx: 0, mb: 1, '&:hover': { bgcolor: "rgba(0,0,0,0.04)" }, borderRadius: 1, p: 0.5, pr: 2 }}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                )}
              </Box>

              <Box sx={{ mt: 4, textAlign: "center" }}>
                {!isShowResult ? (
                  <Button 
                    variant="contained" 
                    size="large"
                    onClick={() => checkAnswer(quiz.id)} 
                    disabled={userAnswers.length === 0}
                    sx={{ bgcolor: lightBlue[400], '&:hover': { bgcolor: lightBlue[500] }, px: 6, py: 1.5, borderRadius: 1, fontWeight: "bold", fontSize: "1.1rem", textTransform: "none", fontFamily: MstaFontFamily }}
                  >
                    解答する
                  </Button>
                ) : (
                  <Box p={3} sx={{ bgcolor: isCorrect ? "#e8f5e9" : "#ffebee", borderRadius: 1, textAlign: "left" }}>
                    <Typography variant="h6" color={isCorrect ? "success.main" : "error.main"} fontFamily={MstaFontFamily} gutterBottom sx={{ display: "flex", alignItems: "center", fontWeight: "bold" }}>
                      {isCorrect ? <><CheckCircle sx={{ mr: 1 }} /> 正解！</> : <><Cancel sx={{ mr: 1 }} /> 不正解...</>}
                    </Typography>
                    {!isCorrect && (
                      <Typography variant="body1" fontFamily={MstaFontFamily} sx={{ mt: 1, color: "text.primary" }}>
                        正解: {quiz.correctAnswers.map(idx => quiz.options[idx]).join(", ")}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between", borderTop: "1px solid", borderColor: grey[200], pt: 3 }}>
                <Button 
                  disabled={currentQuizIndex === 0} 
                  onClick={() => setCurrentQuizIndex(idx => idx - 1)}
                  startIcon={<NavigateBefore />}
                  sx={{ fontFamily: MstaFontFamily, fontWeight: "bold" }}
                >
                  前の問題
                </Button>
                {currentQuizIndex === data.quizItems.length - 1 ? (
                  <Button
                    onClick={() => setResultDialogOpen(true)}
                    endIcon={<NavigateNext />}
                    sx={{ fontFamily: MstaFontFamily, fontWeight: "bold" }}
                    disabled={Object.keys(showResults).length < data.quizItems.length}
                  >
                    結果を見る
                  </Button>
                ) : (
                  <Button
                    onClick={() => setCurrentQuizIndex(idx => idx + 1)}
                    endIcon={<NavigateNext />}
                    sx={{ fontFamily: MstaFontFamily, fontWeight: "bold" }}
                  >
                    次の問題
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        );
      })()}

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={handleReset}
              startIcon={<Refresh />}
              sx={{ fontFamily: MstaFontFamily, color: grey[600] }}
            >
              リセット
            </Button>
          </Box>
        </Grid>
      </Grid>
        </Box>
      </Box>
    </Container>

      {(() => {
        const correctCount = data.quizItems.filter((quiz) => {
          const userAnswers = answers[quiz.id] || [];
          return (
            userAnswers.length === quiz.correctAnswers.length &&
            userAnswers.every((val) => quiz.correctAnswers.includes(val))
          );
        }).length;
        const total = data.quizItems.length;
        return (
          <Dialog open={resultDialogOpen} onClose={() => setResultDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ textAlign: "center", fontFamily: MstaFontFamily, fontWeight: "bold", pt: 4 }}>
              お疲れ様でした！
            </DialogTitle>
            <DialogContent sx={{ textAlign: "center", pb: 2 }}>
              <Typography variant="h3" fontWeight="bold" fontFamily={MstaFontFamily} sx={{ my: 2 }}>
                {correctCount} <Typography component="span" variant="h5" color="text.secondary">/ {total} 問</Typography>
              </Typography>
              <Typography variant="body1" color="text.secondary" fontFamily={MstaFontFamily}>
                正答率: {Math.round((correctCount / total) * 100)}%
              </Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3 }}>
              <Button variant="outlined" onClick={handleResetConfirm} sx={{ fontFamily: MstaFontFamily }}>もう一度挑戦</Button>
              <Button variant="contained" onClick={() => { setResultDialogOpen(false); navigate(-1); }} sx={{ bgcolor: lightBlue[400], '&:hover': { bgcolor: lightBlue[500] } }}>一覧に戻る</Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>リセットの確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            これまでの解答をすべてリセットして最初からやり直しますか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleResetConfirm} color="error" autoFocus>リセット</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
